import {
    type DependencyList,
    type EffectCallback,
    useEffect,
    useEffectEvent,
    useLayoutEffect,
    useRef,
    useState,
} from "react";

export { useMediaQuery } from "./use-media-query.ts";
export {
    type ContactHandoffPath,
    getContactHandoffPath,
    smsHref,
} from "./contact-handoff.ts";
export {
    COARSE_POINTER_QUERY,
    isInAppBrowser,
    isMacOsPlatform,
    NO_HOVER_QUERY,
    useCanUseSmsHandoff,
    useIsInAppBrowser,
    useIsMacOsPlatform,
} from "./use-can-use-sms-handoff.ts";
export {
    MOBILE_VIEWPORT_QUERY,
    useIsMobileViewport,
} from "./use-is-mobile-viewport.ts";

export function useUnmount(dispose: () => void): void {
    const disposeLatest = useEffectEvent(dispose);
    useEffect(() => () => disposeLatest(), []);
}

export function useUnmountLayout(dispose: () => void): void {
    const disposeLatest = useEffectEvent(dispose);
    useLayoutEffect(() => () => disposeLatest(), []);
}

export type LayoutBox = {
    height: number;
    left: number;
    width: number;
};

export type LayoutBoxTarget =
    | { readonly current: HTMLElement | null }
    | (() => HTMLElement | null);

export function useLayoutBox(
    target: LayoutBoxTarget,
    key?: string | number | boolean | null,
): LayoutBox | null {
    const [box, setBox] = useState<LayoutBox | null>(null);
    // oxlint-disable react-hooks/exhaustive-deps -- key is the retrigger; the target is read after the DOM commit
    useLayoutEffect(() => {
        const element =
            typeof target === "function" ? target() : target.current;
        if (!element) {
            setBox(null);
            return;
        }
        const measure = () => {
            const next = {
                height: element.offsetHeight,
                left: element.offsetLeft,
                width: element.offsetWidth,
            };
            setBox((previous) =>
                previous &&
                previous.height === next.height &&
                previous.left === next.left &&
                previous.width === next.width
                    ? previous
                    : next,
            );
        };
        measure();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        const parent = element.parentElement;
        if (parent) {
            observer.observe(parent);
            for (const child of parent.children) {
                if (child instanceof Element) observer.observe(child);
            }
        }
        return () => observer.disconnect();
    }, [key]);
    // oxlint-enable react-hooks/exhaustive-deps
    return box;
}

export function useTextareaAutoHeight(value: string) {
    const ref = useRef<HTMLTextAreaElement | null>(null);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- value is the retrigger; height is read from the DOM after commit
    useLayoutEffect(() => {
        const textarea = ref.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
    }, [value]);
    return ref;
}

export type LayoutGrowScrollAnchor = {
    cursor: number | string;
    scrollHeight: number;
    scrollTop: number;
};

export function growScrollTop(
    anchor: Pick<LayoutGrowScrollAnchor, "scrollHeight" | "scrollTop">,
    scrollHeight: number,
): number {
    return anchor.scrollTop + (scrollHeight - anchor.scrollHeight);
}

export function useLayoutGrowScroll(
    elementRef: { readonly current: HTMLElement | null },
    key: number | string | null,
    anchorRef: { current: LayoutGrowScrollAnchor | null },
    loadingRef?: { current: boolean },
): void {
    // oxlint-disable react-hooks/exhaustive-deps -- refs are stable; key is the prepend trigger
    useLayoutEffect(() => {
        const element = elementRef.current;
        const anchor = anchorRef.current;
        if (!element || !anchor || key === anchor.cursor) return;
        element.scrollTop = growScrollTop(anchor, element.scrollHeight);
        anchorRef.current = null;
        if (loadingRef) loadingRef.current = false;
    }, [key]);
    // oxlint-enable react-hooks/exhaustive-deps
}

export function useUnsafeEffect(
    effect: EffectCallback,
    dependencies?: DependencyList,
): void {
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the framework forwards the caller's dependency list
    useEffect(effect, dependencies);
}

export function useUnsafeLayoutEffect(
    effect: EffectCallback,
    dependencies?: DependencyList,
): void {
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the framework forwards the caller's dependency list
    useLayoutEffect(effect, dependencies);
}
